import io from 'socket.io-client';

const SFU_URL = process.env.REACT_APP_SFU_URL || 'https://myclass.vskul.com';

class WebRTCService {
  constructor() {
    this.socket = null;
    this.localStream = null;
    this.listeners = {};
  }

  async getDevices() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return {
        audioInput: devices.filter(device => device.kind === 'audioinput'),
        videoInput: devices.filter(device => device.kind === 'videoinput'),
      };
    } catch (error) {
      console.error('Error getting devices:', error);
      return { audioInput: [], videoInput: [] };
    }
  }

  async getLocalStream(audioDeviceId = null, videoDeviceId = null) {
    try {
      const constraints = {
        audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
        video: videoDeviceId 
          ? { 
              deviceId: { exact: videoDeviceId },
              width: { ideal: 1280 },
              height: { ideal: 720 },
              aspectRatio: { ideal: 16/9 }
            }
          : {
              width: { ideal: 1280 },
              height: { ideal: 720 },
              aspectRatio: { ideal: 16/9 }
            }
      };

      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
      return this.localStream;
    } catch (error) {
      console.error('Error getting local stream:', error);
      throw error;
    }
  }

  async switchAudioDevice(audioDeviceId) {
    try {
      const audioTrack = this.localStream.getAudioTracks()[0];
      if (!audioTrack) {
        console.error('No audio track found');
        return false;
      }

      const constraints = { audio: { deviceId: { exact: audioDeviceId } } };
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      const newAudioTrack = newStream.getAudioTracks()[0];

      // Replace track in all peer connections
      if (this.listeners.onAudioDeviceChange) {
        this.listeners.onAudioDeviceChange(audioTrack, newAudioTrack);
      }

      audioTrack.stop();
      this.localStream.removeTrack(audioTrack);
      this.localStream.addTrack(newAudioTrack);

      console.log('Audio device switched successfully');
      return true;
    } catch (error) {
      console.error('Error switching audio device:', error);
      return false;
    }
  }

  async switchVideoDevice(videoDeviceId) {
    try {
      const videoTrack = this.localStream.getVideoTracks()[0];
      if (!videoTrack) {
        console.error('No video track found');
        return false;
      }

      const constraints = { video: { deviceId: { exact: videoDeviceId } } };
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      const newVideoTrack = newStream.getVideoTracks()[0];

      // Replace track in all peer connections
      if (this.listeners.onVideoDeviceChange) {
        this.listeners.onVideoDeviceChange(videoTrack, newVideoTrack);
      }

      videoTrack.stop();
      this.localStream.removeTrack(videoTrack);
      this.localStream.addTrack(newVideoTrack);

      console.log('Video device switched successfully');
      return true;
    } catch (error) {
      console.error('Error switching video device:', error);
      return false;
    }
  }

  async connect(token, userEmail = 'Unknown') {
    console.log('Connecting to SFU at:', SFU_URL);
    this.socket = io(SFU_URL, {
      auth: { token },
      query: { userEmail },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      transports: ['websocket', 'polling'],
      secure: true,
      rejectUnauthorized: false,
      withCredentials: true,
      forceNew: true,
    });

    return new Promise((resolve, reject) => {
      this.socket.on('connect', () => {
        console.log('Socket connected:', this.socket.id);
        resolve(this.socket.id);
      });

      this.socket.on('error', (error) => {
        console.error('Socket error:', error);
        reject(error);
      });

      setTimeout(() => reject(new Error('Connection timeout')), 10000);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  stopLocalStream() {
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
    }
  }

  toggleAudio(enabled) {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach(track => {
        track.enabled = enabled;
      });
    }
  }

  toggleVideo(enabled) {
    if (this.localStream) {
      this.localStream.getVideoTracks().forEach(track => {
        track.enabled = enabled;
      });
    }
  }

  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach((callback) => callback(data));
    }
  }
}

export const webrtcService = new WebRTCService();
export default webrtcService;
